import { HttpClient, HttpHeaders } from '@angular/common/http';
// notification.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, switchMap, tap } from 'rxjs';
import { GroupService } from './group.service';
import { SocketService } from './socket.service';
import { environment } from 'src/environments/environment';

interface Notification {
  id: string;
  groupId: string;
  socketId: string;
  completed: boolean;
  message: string;
  isRead: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notificationsSubject: BehaviorSubject<Notification[]> = new BehaviorSubject<Notification[]>([]);
  notifications$: Observable<Notification[]> = this.notificationsSubject.asObservable();

  constructor(
    private http: HttpClient,
    private socketService: SocketService,
    private groupService: GroupService) {

    this.groupService.selectedGroup$.pipe(
      switchMap(selectedGroup => {
        if (selectedGroup) {
          return this.loadNotifications(selectedGroup.id);
        }
        return EMPTY;
      })
    ).subscribe();

    this.socketService.listen('notification', 'notification-service').subscribe((data: Notification) => {
      this.addNotification(data);
    });

    this.socketService.listen('notificationMarkedAsRead', 'notification-service').subscribe((notificationId: string) => {
      this.removeNotification(notificationId);
    });
  }

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('token');
    return new HttpHeaders().set('Authorization', `Bearer ${token}`);
  }

  private removeNotification(notificationId: string): void {
    const currentNotifications = this.notificationsSubject.getValue();
    const updatedNotifications = currentNotifications.filter(notification => notification.id !== notificationId);
    this.notificationsSubject.next(updatedNotifications);
  }

  private addNotification(notification: Notification): void {
    const currentNotifications = this.notificationsSubject.getValue();
    const updatedNotifications = [...currentNotifications, notification];
    this.notificationsSubject.next(updatedNotifications);
    if (notification.completed) {
      this.playNotificationSound();
    }
  }

  private playNotificationSound() {
    const audio = new Audio('/assets/sounds/new-notification.wav');
    audio.play();
  }


  loadNotifications(groupId: string): Observable<Notification[]> {
    this.notificationsSubject.next([]);
    return this.http.get<Notification[]>(`${environment.API_URL}/api/notifications/${groupId}`, { headers: this.getAuthHeaders() }).pipe(
      tap((result: any) => {
        this.notificationsSubject.next(result.data);
      })
    );
  }

  markNotificationAsRead(notificationId: string): Observable<any> {
    return this.http.put(`${environment.API_URL}/api/notifications/${notificationId}/read`, {}, { headers: this.getAuthHeaders() })
      .pipe(tap(
        response => {
          // Find the notification in the current notifications array
          const currentNotifications = this.notificationsSubject.getValue();
          const notificationIndex = currentNotifications.findIndex(notification => notification.id === notificationId);

          if (notificationIndex !== -1) {
            // Update the notification's isRead property
            const updatedNotifications = [...currentNotifications];
            updatedNotifications[notificationIndex] = {
              ...updatedNotifications[notificationIndex],
              isRead: true
            };

            // Emit the updated notifications
            this.notificationsSubject.next(updatedNotifications);
          }
        },
        error => {
          console.error('Error marking notification as read', error);
        }
      ));
  }
}